import { forwardRef, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { MY_PAGE_MENU_ITEMS, SELLER_MENU_ITEMS } from '@configs/routes';
import type { MenuItemConfig } from '@configs/routes';
import { getAboutMe } from '@features/user/service';

type MyPageSidebarProps = {
  isOpen: boolean;
};

export const MyPageSidebar = forwardRef<HTMLDivElement, MyPageSidebarProps>(({ isOpen }, ref) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const [isSeller, setIsSeller] = useState(false);
  const [isLoadingSellerStatus, setIsLoadingSellerStatus] = useState(true);

  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1080px)');
    const handleChange = () => setIsMobileViewport(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // 전문가 여부 확인
  useEffect(() => {
    const checkSellerStatus = async () => {
      try {
        const response = await getAboutMe();
        if (response.success && response.data) {
          // sellerId가 null이 아니거나 seller가 null이 아니면 전문가
          const hasSeller = response.data.sellerId !== null && response.data.sellerId !== undefined;
          setIsSeller(hasSeller);
        }
      } catch (error) {
        console.error('전문가 상태 확인 실패:', error);
        setIsSeller(false);
      } finally {
        setIsLoadingSellerStatus(false);
      }
    };

    void checkSellerStatus();
  }, []);

  // 활성 상태 체크 함수
  const isActive = (menuItem: MenuItemConfig): boolean => {
    // startsWithPaths가 있으면 해당 경로들로 시작하는지 체크
    if (menuItem.startsWithPaths) {
      return menuItem.startsWithPaths.some((path) => location.pathname.startsWith(path));
    }

    // activePaths가 있으면 정확한 경로 매칭
    if (menuItem.activePaths) {
      return menuItem.activePaths.some((path) => location.pathname === path);
    }

    // 기본값: fullPath와 정확히 일치하는지 체크
    return location.pathname === menuItem.fullPath;
  };

  const withRef = (path: string) => (refCode ? `${path}?ref=${refCode}` : path);

  const MenuItem = ({
    to,
    iconOff,
    iconOn,
    title,
    isActive,
  }: {
    to: string;
    iconOff: string;
    iconOn: string;
    title: string;
    isActive: boolean;
  }) => {
    const itemStyle: CSSProperties = { position: 'relative', width: '100%' };
    const linkStyle: CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontWeight: isActive ? 600 : 400,
      fontSize: '1rem',
      lineHeight: '100%',
      textTransform: 'uppercase',
      color: isActive ? '#019ca3' : '#000',
      whiteSpace: 'nowrap',
    };

    const iconWrapStyle: CSSProperties = { position: 'relative', width: '1.25rem', aspectRatio: '1 / 1' };

    const imgBaseStyle: CSSProperties = {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    };

    const imgOnStyle: CSSProperties = { ...imgBaseStyle, opacity: isActive ? 1 : 0 };
    const imgOffStyle: CSSProperties = { ...imgBaseStyle, opacity: isActive ? 0 : 1 };

    return (
      <div className={`item ${isActive ? 'on' : ''}`} style={itemStyle}>
        <NavLink to={to} className="link" style={linkStyle}>
          <div className="icon" style={iconWrapStyle}>
            <img src={iconOff} alt="" className="img off" style={imgOffStyle} />
            <img src={iconOn} alt="" className="img on" style={imgOnStyle} />
          </div>
          <span className="tit">{title}</span>
        </NavLink>
      </div>
    );
  };

  const sidebarStyle = useMemo<CSSProperties>(() => {
    if (isMobileViewport) {
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '84%',
        maxWidth: '365px',
        height: '100%',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        overflowY: 'scroll',
        overflowX: 'hidden',
        msOverflowStyle: 'none',
        zIndex: 250,
        display: 'flex',
        flexDirection: 'column',
        gap: '2.625rem',
        paddingTop: '3.125rem',
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.2s ease',
      };
    }

    return {
      position: 'relative',
      width: '14.25rem',
      paddingTop: '3.125rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '2.625rem',
    };
  }, [isMobileViewport, isOpen]);

  const sideItemStyle: CSSProperties = useMemo(() => ({ position: 'relative', width: '100%' }), []);

  const sideTitleStyle: CSSProperties = useMemo(
    () => ({
      fontWeight: 600,
      fontSize: '1.375rem',
      lineHeight: '100%',
      textTransform: 'uppercase',
      color: '#000',
      marginBottom: '1rem',
    }),
    [],
  );

  const menuListStyle: CSSProperties = useMemo(() => ({ display: 'flex', flexDirection: 'column' }), []);

  const menuBoxStyle: CSSProperties = useMemo(
    () => ({
      borderTop: '1px solid #acacc5',
      padding: '1.125rem 0 1.125rem 0.688rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.625rem',
    }),
    [],
  );

  return (
    <div ref={ref} className={`my_menu ${isOpen ? 'open' : ''}`} style={sidebarStyle}>
      {/* 마이페이지 섹션 */}
      <div className="sideitem" style={sideItemStyle}>
        <p className="side_tit" style={sideTitleStyle}>
          마이페이지
        </p>
        <div className="menu_list" style={menuListStyle}>
          <div className="menu_box" style={menuBoxStyle}>
            {MY_PAGE_MENU_ITEMS.slice(0, 4).map((item) => (
              <MenuItem
                key={item.fullPath}
                to={withRef(item.fullPath)}
                iconOff={item.iconOff}
                iconOn={item.iconOn}
                title={item.title}
                isActive={isActive(item)}
              />
            ))}
          </div>
          <div className="menu_box" style={menuBoxStyle}>
            {MY_PAGE_MENU_ITEMS.slice(4).map((item) => (
              <MenuItem
                key={item.fullPath}
                to={withRef(item.fullPath)}
                iconOff={item.iconOff}
                iconOn={item.iconOn}
                title={item.title}
                isActive={isActive(item)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 전문가 관리 섹션 */}
      {!isLoadingSellerStatus && (
        <div className="sideitem" style={sideItemStyle}>
          <p className="side_tit" style={sideTitleStyle}>
            전문가 관리
          </p>
          <div className="menu_list" style={menuListStyle}>
            <div className="menu_box" style={menuBoxStyle}>
              {SELLER_MENU_ITEMS.filter((item) => {
                // requiresSeller가 true인 경우 전문가일 때만 표시
                // requiresSeller가 false인 경우 전문가가 아닐 때만 표시
                // requiresSeller가 없거나 undefined인 경우 항상 표시
                if (item.requiresSeller === true) {
                  return isSeller;
                }
                if (item.requiresSeller === false) {
                  return !isSeller;
                }
                return true;
              }).map((item) => (
                <MenuItem
                  key={item.fullPath}
                  to={withRef(item.fullPath)}
                  iconOff={item.iconOff}
                  iconOn={item.iconOn}
                  title={item.title}
                  isActive={isActive(item)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

MyPageSidebar.displayName = 'MyPageSidebar';
